import {
  Box,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  LOOKUP_MASTERS,
  lookupItemsToStringLabels,
  type ApiCustomerLocationCapacityRow,
  type ApiCustomerLocationStorageCapacity,
  type AssociateCustomerLocationInput,
  type CustomerAvailableLocation,
  type LookupDetailItem,
  type OperatorBookingSettingsResponse,
  type UpdateCustomerLocationInput,
  rejectMessage,
} from "api-client";
import {
  ActionButton,
  DrawerSearchableSelectField,
  IconClose,
  RadioControl,
  useLookup,
} from "design-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { customerLocationsService } from "../../../shared/services/customer-locations.service";
import { lookupService } from "../../../shared/services/lookup.service";
import {
  AssociateLocationStorageSection,
  buildCreateStorageForm,
  EMPTY_STORAGE_BRANCH_FORM,
  validateStorageBranchForm,
  type StorageBranchFormState,
} from "./associate-location-storage-section.component";
import {
  OPERATOR_BOOKING_SETTINGS_OPERATOR_ID,
  operatorBookingSettingsService,
} from "../../../shared/services/operator-booking-settings.service";

const STEP1_DESCRIPTION =
  "Select from existing locations. You can set capacity and day rate for this customer at this site once you select a location.";

type StorageTypePickValue = "Stack" | "Wheeled" | "Both";

const STORAGE_TYPE_PICK_OPTIONS: ReadonlyArray<{ value: StorageTypePickValue; label: string }> = [
  { value: "Stack", label: "Stack" },
  { value: "Wheeled", label: "Wheeled" },
  { value: "Both", label: "Both" },
];

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_STACK_WARNING_THRESHOLD = 100;
const DEFAULT_WHEELED_WARNING_THRESHOLD = 80;
/** Lookup master 12 — warning unlimited; used when associate flow leaves enforcement mode unset. */
const DEFAULT_ASSOCIATE_ENFORCEMENT_MODE_ID = 26;

export type AssociateLocationDrawerMode = "create" | "edit";

export interface AssociateLocationDrawerProps {
  open: boolean;
  onClose: () => void;
  mode: AssociateLocationDrawerMode;
  customerId: string;
  excludeLocationIds: number[];
  editRow?: ApiCustomerLocationCapacityRow | null;
  onSubmitCreate: (body: AssociateCustomerLocationInput) => Promise<void>;
  onSubmitEdit: (locationId: number, body: UpdateCustomerLocationInput) => Promise<void>;
  submitting?: boolean;
  submitError?: string | null;
}

type DrawerStep = "pick" | "configure";

export function AssociateLocationDrawer(props: AssociateLocationDrawerProps) {
  const {
    open,
    onClose,
    mode,
    customerId,
    excludeLocationIds,
    editRow = null,
    onSubmitCreate,
    onSubmitEdit,
    submitting = false,
    submitError = null,
  } = props;

  const isEditMode = mode === "edit";

  const [step, setStep] = useState<DrawerStep>("pick");
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  const [availableLocations, setAvailableLocations] = useState<CustomerAvailableLocation[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [pickError, setPickError] = useState("");

  const [storageType, setStorageType] = useState("");
  const [stackForm, setStackForm] = useState<StorageBranchFormState>(EMPTY_STORAGE_BRANCH_FORM);
  const [wheeledForm, setWheeledForm] = useState<StorageBranchFormState>(EMPTY_STORAGE_BRANCH_FORM);
  const [configureErrors, setConfigureErrors] = useState<Record<string, string>>({});
  const [defaultStackDayRate, setDefaultStackDayRate] = useState("");
  const [defaultWheeledDayRate, setDefaultWheeledDayRate] = useState("");

  const configureStepActive = step === "configure";
  const showPickStep = step === "pick";
  const showConfigureStep = step === "configure";

  const { items: enforcementLookupItems, error: enforcementLookupError } = useLookup(
    lookupService,
    LOOKUP_MASTERS.enforcementMode,
    { enabled: open && configureStepActive },
  );

  const enforcementModeOptions = useMemo(
    () => lookupItemsToStringLabels(enforcementLookupItems),
    [enforcementLookupItems],
  );

  const storageTypeRef = useRef(storageType);
  storageTypeRef.current = storageType;
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drawerOpenSessionRef = useRef(false);
  const editPrefilledRef = useRef(false);

  const excludeSet = useMemo(() => new Set(excludeLocationIds), [excludeLocationIds]);

  const selectableLocations = useMemo(
    () => availableLocations.filter((l) => !excludeSet.has(l.locationId)),
    [availableLocations, excludeSet],
  );

  const selectedLocation = useMemo(() => {
    const fromSearch = selectableLocations.find((l) => l.locationId === selectedLocationId);
    if (fromSearch) return fromSearch;
    if (selectedLocationId != null && editRow && editRow.locationId === selectedLocationId) {
      return {
        locationId: editRow.locationId,
        locationName: editRow.locationName,
      };
    }
    return null;
  }, [editRow, selectableLocations, selectedLocationId]);

  const locationSelectOptions = useMemo(() => {
    const options = selectableLocations.map((l) => ({
      value: String(l.locationId),
      label: l.locationName.trim() || `Location ${l.locationId}`,
    }));
    if (
      selectedLocationId != null &&
      editRow &&
      editRow.locationId === selectedLocationId &&
      !options.some((opt) => opt.value === String(selectedLocationId))
    ) {
      options.unshift({
        value: String(editRow.locationId),
        label: editRow.locationName.trim() || `Location ${editRow.locationId}`,
      });
    }
    return options;
  }, [editRow, selectableLocations, selectedLocationId]);

  const showStackSection = storageType === "Stack" || storageType === "Both";
  const showWheeledSection = storageType === "Wheeled" || storageType === "Both";

  const resetConfigureFieldsOnly = useCallback(() => {
    setStackForm(buildCreateStorageForm(defaultStackDayRate));
    setWheeledForm(buildCreateStorageForm(defaultWheeledDayRate));
    setConfigureErrors({});
  }, [defaultStackDayRate, defaultWheeledDayRate]);

  const resetCreatePickForm = useCallback(() => {
    setStorageType("");
    resetConfigureFieldsOnly();
  }, [resetConfigureFieldsOnly]);

  const prefillConfigureFromRow = useCallback(
    (row: ApiCustomerLocationCapacityRow) => {
      setStorageType(inferStorageTypeLabelFromRow(row));
      setStackForm(branchFormFromApi(row.stack, enforcementLookupItems));
      setWheeledForm(branchFormFromApi(row.wheeled, enforcementLookupItems));
      setConfigureErrors({});
    },
    [enforcementLookupItems],
  );

  const handleStorageTypeChange = useCallback((next: string) => {
    storageTypeRef.current = next;
    setStorageType(next);
    setConfigureErrors((prev) => {
      const cleared = { ...prev };
      delete cleared.storageType;
      return cleared;
    });
  }, []);

  useEffect(() => {
    if (!open) {
      drawerOpenSessionRef.current = false;
      editPrefilledRef.current = false;
      return;
    }

    if (drawerOpenSessionRef.current) return;
    drawerOpenSessionRef.current = true;

    setSearchQuery("");
    setAvailableLocations([]);
    setLocationsError(null);
    setPickError("");
    setSelectedLocationId(null);
    setStep("pick");

    if (isEditMode && editRow) {
      setSelectedLocationId(editRow.locationId);
      prefillConfigureFromRow(editRow);
      return;
    }

    resetCreatePickForm();
  }, [editRow, isEditMode, open, prefillConfigureFromRow, resetCreatePickForm]);

  useEffect(() => {
    if (!open || isEditMode) return;

    let cancelled = false;
    void operatorBookingSettingsService
      .getBookingSettings(OPERATOR_BOOKING_SETTINGS_OPERATOR_ID)
      .then((res: OperatorBookingSettingsResponse) => {
        if (cancelled) return;
        const stack = Number.isFinite(res.data.stackDayRate) ? String(res.data.stackDayRate) : "";
        const wheeled = Number.isFinite(res.data.wheeledDayRate)
          ? String(res.data.wheeledDayRate)
          : "";
        setDefaultStackDayRate(stack);
        setDefaultWheeledDayRate(wheeled);
        setStackForm((prev) => ({
          ...prev,
          dayRate: prev.dayRate.trim() === "" ? stack : prev.dayRate,
        }));
        setWheeledForm((prev) => ({
          ...prev,
          dayRate: prev.dayRate.trim() === "" ? wheeled : prev.dayRate,
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setDefaultStackDayRate("");
        setDefaultWheeledDayRate("");
      });

    return () => {
      cancelled = true;
    };
  }, [isEditMode, open]);

  useEffect(() => {
    if (!open || step !== "pick") return;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const term = searchQuery.trim();
    if (!term) {
      setAvailableLocations([]);
      setLocationsLoading(false);
      setLocationsError(null);
      return;
    }

    setLocationsLoading(true);
    setLocationsError(null);

    let cancelled = false;
    searchDebounceRef.current = setTimeout(() => {
      void customerLocationsService
        .searchCustomerAvailableLocations(customerId, term)
        .then((res) => {
          if (cancelled) return;
          setLocationsError(null);
          setAvailableLocations(res.data?.locations ?? []);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setLocationsError(rejectMessage(e, "Failed to search locations."));
          setAvailableLocations([]);
        })
        .finally(() => {
          if (!cancelled) setLocationsLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [customerId, open, searchQuery, step]);

  useEffect(() => {
    if (!open || !isEditMode || !editRow || enforcementLookupItems.length === 0) return;
    if (editPrefilledRef.current) return;
    editPrefilledRef.current = true;
    prefillConfigureFromRow(editRow);
  }, [editRow, enforcementLookupItems, isEditMode, open, prefillConfigureFromRow]);

  const handleDrawerClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleLocationValueChange = useCallback(
    (next: string | null) => {
      const nextId = next != null ? Number(next) : null;
      setSelectedLocationId((prev) => {
        if (prev !== nextId) {
          resetCreatePickForm();
          if (isEditMode) {
            editPrefilledRef.current = true;
          }
        }
        return nextId;
      });
      setPickError("");
    },
    [isEditMode, resetCreatePickForm],
  );

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleFooterCancel = useCallback(() => {
    if (step === "configure") {
      setStep("pick");
      if (!isEditMode) {
        resetConfigureFieldsOnly();
      }
      return;
    }
    handleDrawerClose();
  }, [handleDrawerClose, isEditMode, resetConfigureFieldsOnly, step]);

  const handleLocationBlur = useCallback(() => {
    if (selectedLocationId == null) {
      setPickError("Select a location to continue.");
    }
  }, [selectedLocationId]);

  const blurConfigureField = useCallback((field: string, message: string | undefined) => {
    setConfigureErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  }, []);

  const clearConfigureError = useCallback((errorKey: string) => {
    setConfigureErrors((prev) => {
      if (!prev[errorKey]) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  }, []);

  const handleStackFieldChange = useCallback(
    (field: keyof StorageBranchFormState, value: string) => {
      setStackForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleWheeledFieldChange = useCallback(
    (field: keyof StorageBranchFormState, value: string) => {
      setWheeledForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleStackFieldBlur = useCallback(
    (field: keyof StorageBranchFormState) => {
      const errors = validateStorageBranchForm("stack", stackForm);
      const key = stackErrorKey(field);
      blurConfigureField(key, errors[key]);
    },
    [blurConfigureField, stackForm],
  );

  const handleWheeledFieldBlur = useCallback(
    (field: keyof StorageBranchFormState) => {
      const errors = validateStorageBranchForm("wheeled", wheeledForm);
      const key = wheeledErrorKey(field);
      blurConfigureField(key, errors[key]);
    },
    [blurConfigureField, wheeledForm],
  );

  const handleContinue = useCallback(() => {
    let hasError = false;
    if (selectedLocationId == null) {
      setPickError("Select a location to continue.");
      hasError = true;
    } else {
      setPickError("");
    }
    if (!storageType.trim()) {
      blurConfigureField("storageType", "Select a storage type.");
      hasError = true;
    } else {
      blurConfigureField("storageType", undefined);
    }
    if (hasError) return;
    setStep("configure");
    if (!isEditMode) {
      resetConfigureFieldsOnly();
    }
  }, [blurConfigureField, isEditMode, resetConfigureFieldsOnly, selectedLocationId, storageType]);

  const validateConfigure = useCallback((): boolean => {
    const next: Record<string, string> = {};
    if (!storageType.trim()) {
      next.storageType = "Select a storage type.";
    }
    if (showStackSection) {
      Object.assign(next, validateStorageBranchForm("stack", stackForm));
    }
    if (showWheeledSection) {
      Object.assign(next, validateStorageBranchForm("wheeled", wheeledForm));
    }
    setConfigureErrors(next);
    return Object.keys(next).length === 0;
  }, [showStackSection, showWheeledSection, stackForm, storageType, wheeledForm]);

  const handleSave = useCallback(() => {
    if (!validateConfigure()) return;

    const body = buildLocationMutationBody({
      storageType,
      stackForm: showStackSection ? stackForm : null,
      wheeledForm: showWheeledSection ? wheeledForm : null,
      enforcementLookupItems,
      defaultEnforcementModeId: DEFAULT_ASSOCIATE_ENFORCEMENT_MODE_ID,
    });

    if (isEditMode && editRow) {
      const locationId = selectedLocationId ?? editRow.locationId;
      void onSubmitEdit(locationId, body).then(() => {
        handleDrawerClose();
      });
      return;
    }

    if (selectedLocationId == null) return;

    void onSubmitCreate({
      locationId: selectedLocationId,
      ...body,
    }).then(() => {
      handleDrawerClose();
    });
  }, [
    editRow,
    enforcementLookupItems,
    handleDrawerClose,
    isEditMode,
    onSubmitCreate,
    onSubmitEdit,
    selectedLocationId,
    showStackSection,
    showWheeledSection,
    stackForm,
    storageType,
    validateConfigure,
    wheeledForm,
  ]);

  const drawerTitle =
    showPickStep && !isEditMode
      ? "Associate location"
      : (selectedLocation?.locationName.trim() || editRow?.locationName.trim() || "Location");

  const locationFieldOpen = open && showPickStep;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgb(255 255 255 / 50%)" },
        },
        paper: { className: "user-form-drawer__paper" },
      }}
    >
      <Box className="user-form-drawer associate-location-drawer">
        <Stack spacing={2.5} className="user-form-drawer__body">
          <Stack spacing={1.25} className="user-form-drawer__header-block">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography className="user-form-drawer__title" component="h2">
                {drawerTitle}
              </Typography>
              <IconButton
                type="button"
                onClick={handleDrawerClose}
                aria-label="Close"
                className="user-form-drawer__close"
              >
                <IconClose aria-hidden size={24} />
              </IconButton>
            </Stack>
            {showPickStep ? (
              <Typography className="user-form-drawer__description" variant="body2">
                {STEP1_DESCRIPTION}
              </Typography>
            ) : null}
          </Stack>

          <Box className="user-form-drawer__fields-scroll">
            <Stack spacing={2.5} className="user-form-drawer__fields">
              {showPickStep ? (
                <>
                  <DrawerSearchableSelectField
                    open={locationFieldOpen}
                    label="Select location"
                    required
                    placeholder="Search locations"
                    options={locationSelectOptions}
                    value={selectedLocationId != null ? String(selectedLocationId) : null}
                    onValueChange={(next) => handleLocationValueChange(next)}
                    onSearchQueryChange={handleSearchQueryChange}
                    onBlur={handleLocationBlur}
                    loading={locationsLoading}
                    loadError={locationsError}
                    emptyListMessage="No locations are available to associate. All known sites may already be linked."
                    noMatchMessage="No locations match your search."
                    loadingMessage="Loading locations…"
                    errorText={pickError || null}
                    suggestionsListAriaLabel="Location search results"
                    fieldDisabled={isEditMode}
                  />
                  <FormControl
                    fullWidth
                    error={Boolean(configureErrors.storageType)}
                    className="associate-location-drawer__storage-type"
                  >
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={0} className="input-label-row">
                        <FormLabel className="input-label">Storage type</FormLabel>
                        <Typography component="span" className="input-required">
                          *
                        </Typography>
                      </Stack>
                      <RadioGroup
                        row
                        className="associate-location-drawer__radio-row"
                        aria-label="Storage type"
                        value={storageType}
                        onChange={(e) => handleStorageTypeChange(e.target.value)}
                      >
                        {STORAGE_TYPE_PICK_OPTIONS.map((opt) => (
                          <FormControlLabel
                            key={opt.value}
                            value={opt.value}
                            control={<RadioControl />}
                            label={opt.label}
                          />
                        ))}
                      </RadioGroup>
                      {configureErrors.storageType ? (
                        <FormHelperText className="input-helper-text">
                          {configureErrors.storageType}
                        </FormHelperText>
                      ) : null}
                    </Stack>
                  </FormControl>
                </>
              ) : null}
              {showConfigureStep ? (
                <>
                  {showStackSection ? (
                    <AssociateLocationStorageSection
                      title="Stack storage"
                      errorPrefix="stack"
                      form={stackForm}
                      errors={configureErrors}
                      enforcementModeOptions={enforcementModeOptions}
                      enforcementLookupError={enforcementLookupError}
                      onFieldChange={handleStackFieldChange}
                      onFieldBlur={handleStackFieldBlur}
                      onClearError={clearConfigureError}
                    />
                  ) : null}
                  {showStackSection && showWheeledSection ? (
                    <Divider className="associate-location-drawer__section-divider" />
                  ) : null}
                  {showWheeledSection ? (
                    <AssociateLocationStorageSection
                      title="Wheeled storage"
                      errorPrefix="wheeled"
                      form={wheeledForm}
                      errors={configureErrors}
                      enforcementModeOptions={enforcementModeOptions}
                      enforcementLookupError={enforcementLookupError}
                      onFieldChange={handleWheeledFieldChange}
                      onFieldBlur={handleWheeledFieldBlur}
                      onClearError={clearConfigureError}
                    />
                  ) : null}
                  {submitError ? (
                    <Typography component="p" color="error" variant="body2">
                      {submitError}
                    </Typography>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1.25} className="user-form-drawer__actions">
            {showPickStep ? (
              <ActionButton
                type="button"
                actionVariant="contained-primary"
                size="small"
                onClick={handleContinue}
                disabled={!isEditMode && (locationsLoading || Boolean(locationsError))}
              >
                Continue
              </ActionButton>
            ) : showConfigureStep ? (
              <ActionButton
                type="button"
                actionVariant="contained-primary"
                size="small"
                onClick={handleSave}
                disabled={submitting}
              >
                Save
              </ActionButton>
            ) : null}
            <ActionButton type="button" actionVariant="neutral" onClick={handleFooterCancel} size="small">
              Cancel
            </ActionButton>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}

interface BuildLocationMutationBodyParams {
  storageType: string;
  stackForm: StorageBranchFormState | null;
  wheeledForm: StorageBranchFormState | null;
  enforcementLookupItems: LookupDetailItem[];
  /** When enforcement mode is unset, wire this lookup id (associate + edit). */
  defaultEnforcementModeId?: number | null;
}

function buildLocationMutationBody(
  params: BuildLocationMutationBodyParams,
): UpdateCustomerLocationInput {
  const wireStorageType = mapStorageTypeLabelToWire(params.storageType);
  const defaultEnforcementModeId = params.defaultEnforcementModeId ?? null;

  const stack =
    params.stackForm != null
      ? buildBranchRulesInput(
          params.stackForm,
          params.enforcementLookupItems,
          DEFAULT_STACK_WARNING_THRESHOLD,
          defaultEnforcementModeId,
        )
      : null;
  const wheeled =
    params.wheeledForm != null
      ? buildBranchRulesInput(
          params.wheeledForm,
          params.enforcementLookupItems,
          DEFAULT_WHEELED_WARNING_THRESHOLD,
          defaultEnforcementModeId,
        )
      : null;

  return {
    storageType: wireStorageType,
    stack,
    wheeled,
  };
}

function buildBranchRulesInput(
  form: StorageBranchFormState,
  enforcementLookupItems: LookupDetailItem[],
  warningThreshold: number,
  defaultEnforcementModeId: number | null,
) {
  const dayRate = parseOptionalDayRate(form.dayRate);
  return {
    capacity: parsePositiveInt(form.capacity),
    graceCapacity: parseNonNegativeInt(form.graceCapacity),
    enforcementMode: resolveEnforcementModeId(
      form.enforcementMode,
      enforcementLookupItems,
      defaultEnforcementModeId,
    ),
    warningThreshold,
    dayRate,
  };
}

function branchFormFromApi(
  branch: ApiCustomerLocationStorageCapacity | null,
  enforcementLookupItems: LookupDetailItem[],
): StorageBranchFormState {
  if (!branch) return EMPTY_STORAGE_BRANCH_FORM;
  return {
    capacity: String(branch.capacity),
    graceCapacity: String(branch.graceCapacity),
    enforcementMode: resolveEnforcementModeLabel(branch.enforcementMode, enforcementLookupItems),
    dayRate: formatDecimalField(branch.dayRate),
  };
}

function stackErrorKey(field: keyof StorageBranchFormState): string {
  const map: Record<keyof StorageBranchFormState, string> = {
    capacity: "stackCapacity",
    graceCapacity: "stackGraceCapacity",
    enforcementMode: "stackEnforcementMode",
    dayRate: "stackDayRate",
  };
  return map[field];
}

function wheeledErrorKey(field: keyof StorageBranchFormState): string {
  const map: Record<keyof StorageBranchFormState, string> = {
    capacity: "wheeledCapacity",
    graceCapacity: "wheeledGraceCapacity",
    enforcementMode: "wheeledEnforcementMode",
    dayRate: "wheeledDayRate",
  };
  return map[field];
}

function inferStorageTypeLabelFromRow(row: ApiCustomerLocationCapacityRow): string {
  if (row.stack && row.wheeled) return "Both";
  if (row.stack) return "Stack";
  if (row.wheeled) return "Wheeled";
  return "";
}

function mapStorageTypeLabelToWire(label: string): UpdateCustomerLocationInput["storageType"] {
  if (label === "Stack") return "stack";
  if (label === "Wheeled") return "wheeled";
  if (label === "Both") return "both";
  return "both";
}

function resolveEnforcementModeId(
  label: string,
  items: LookupDetailItem[],
  defaultWhenUnset: number | null = null,
): number {
  const trimmed = label.trim();
  if (!trimmed) {
    return defaultWhenUnset ?? 0;
  }
  const match = items.find((item) => item.value.trim() === trimmed);
  return match?.id ?? defaultWhenUnset ?? 0;
}

function resolveEnforcementModeLabel(id: number, items: LookupDetailItem[]): string {
  const match = items.find((item) => item.id === id);
  return match?.value.trim() ?? "";
}

function formatDecimalField(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  return String(value);
}

function parsePositiveInt(raw: string): number {
  const n = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parseNonNegativeInt(raw: string): number {
  const n = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Blank → null; 0 → 0; other finite → value (FR-DRN-003). */
function parseOptionalDayRate(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number.parseFloat(trimmed);
  return Number.isFinite(n) ? n : null;
}
